import { useSignIn, useSignUp } from "@clerk/react";
import type React from "react";
import { useCallback, useMemo, useRef, useState, type RefCallback } from "react";

import { ErrorHandler } from "@/web/lib/errors";

export type AuthMethod = "email" | null;

export type PendingEmailAction = "send-link" | "send-code" | "verify" | null;

type EmailCodeBranch = "sign-in" | "sign-up";

export type AuthnFlowState = {
  selectedMethod: AuthMethod;
  emailSent: boolean;
  emailCodeAwaitingVerify: boolean;
  pendingEmailAction: PendingEmailAction;
  isSocialLoading: boolean;
  errorMessage?: string;
  /** Pre-filled address for the email field; see `UseAuthnDialogFlowOptions`. */
  defaultEmail?: string | undefined;
};

export type AuthnFlowActions = {
  setSelectedMethod: (method: AuthMethod) => void;
  handleGoogleAuth: () => Promise<void>;
  handleSendMagicLink: () => Promise<void>;
  handleSendEmailCode: () => Promise<void>;
  handleVerifyEmailCode: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

export type AuthnFlowRefs = {
  emailInputRef: React.RefObject<HTMLInputElement | null>;
  /** Clears, focuses, and tracks the OTP field when it mounts (no useEffect in the form). */
  codeInputRefCallback: RefCallback<HTMLInputElement>;
};

export type AuthnDialogFlow = {
  state: AuthnFlowState;
  actions: AuthnFlowActions;
  refs: AuthnFlowRefs;
};

function getErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "errors" in error) {
    const clerkError = error as { errors?: Array<{ longMessage?: string; message?: string }> };
    const firstError = clerkError.errors?.[0];
    if (firstError?.longMessage) return firstError.longMessage;
    if (firstError?.message) return firstError.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
}

function getErrorCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("errors" in error)) {
    return undefined;
  }

  const clerkError = error as { errors?: Array<{ code?: string }> };
  return clerkError.errors?.[0]?.code;
}

function isFormIdentifierNotFound(error: unknown): boolean {
  return getErrorCode(error) === "form_identifier_not_found";
}

function readTrimmedInput(el: HTMLInputElement | null): string | undefined {
  const raw = el?.value?.trim();
  return raw ? raw : undefined;
}

/** Post-auth destination: preserve deep links; send homepage logins to the dashboard. */
function resolveRedirectUrl(
  pathname = typeof window !== "undefined" ? window.location.pathname : "/",
  search = typeof window !== "undefined" ? window.location.search : "",
): string {
  if (pathname === "/") {
    return "/profile";
  }
  return `${pathname}${search}`;
}

function buildMagicLinkVerificationUrl(redirectTo: string) {
  if (typeof window === "undefined") {
    return "";
  }

  // This app uses Clerk custom-flow APIs (not Account Portal pages), so callback routing
  // is app-owned and configured in code via verification/redirect URLs.
  const callbackUrl = new URL("/callback/auth", window.location.origin);
  callbackUrl.searchParams.set("redirect_url", redirectTo);
  return callbackUrl.toString();
}

export type UseAuthnDialogFlowOptions = {
  /**
   * Pre-fills the email field and opens straight to the email branch, skipping
   * the method picker. Used by invited onboarding, where the link carries the
   * address we already hold on the invited-brand draft (`?email=`).
   *
   * Display convenience ONLY — never identity. It comes from a user-editable
   * query param, and the user still has to prove control of the inbox via
   * Clerk. The server re-checks the Clerk-derived email against the draft in
   * `assertDraftUsable`; nothing here can widen that.
   */
  defaultEmail?: string | undefined;
  /**
   * App path to land on after any sign-in method completes, overriding the
   * default (the current page, or `/profile` from the homepage). For entry
   * points that mean "sign in, then go to X", e.g. the homepage onboarding
   * tiles.
   */
  redirectTo?: string | undefined;
};

export function useAuthnDialogFlow({ defaultEmail, redirectTo }: UseAuthnDialogFlowOptions = {}) {
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  // Resolved per call: the default depends on the page the dialog is open on.
  const postAuthUrl = useCallback(() => redirectTo ?? resolveRedirectUrl(), [redirectTo]);

  // A pre-filled address means the method picker has nothing left to ask, so
  // land directly on the email branch.
  const [selectedMethod, setSelectedMethodState] = useState<AuthMethod>(
    defaultEmail ? "email" : null,
  );
  const [emailSent, setEmailSent] = useState(false);
  const [emailCodeAwaitingVerify, setEmailCodeAwaitingVerify] = useState(false);
  const [emailCodeBranch, setEmailCodeBranch] = useState<EmailCodeBranch | null>(null);
  const [pendingEmailAction, setPendingEmailAction] = useState<PendingEmailAction>(null);
  const [isSocialLoading, setIsSocialLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const codeInputRefCallback = useCallback<RefCallback<HTMLInputElement>>((el) => {
    codeInputRef.current = el;
    if (!el) {
      return;
    }
    el.value = "";
    queueMicrotask(() => {
      el.focus({ preventScroll: true });
    });
  }, []);

  const verificationUrl = useMemo(() => {
    return buildMagicLinkVerificationUrl(postAuthUrl());
  }, [postAuthUrl]);

  const resetFlow = useCallback(() => {
    setSelectedMethodState(null);
    setEmailSent(false);
    setEmailCodeAwaitingVerify(false);
    setEmailCodeBranch(null);
    setPendingEmailAction(null);
    setErrorMessage(undefined);
    void signIn?.reset();
    void signUp?.reset();
  }, [signIn, signUp]);

  const setSelectedMethod = useCallback(
    (method: AuthMethod) => {
      if (method === null) {
        resetFlow();
        return;
      }
      setSelectedMethodState(method);
      if (method === "email") {
        queueMicrotask(() => {
          requestAnimationFrame(() => {
            emailInputRef.current?.focus({ preventScroll: true });
          });
        });
      }
    },
    [resetFlow],
  );

  const sendEmailMagicLink = useCallback(
    async (emailAddress: string) => {
      if (!signIn || !signUp) {
        return false;
      }

      // Force a fresh SignIn resource. Without this, signIn.emailLink.sendLink()
      // can reuse a stale sign_in id left over from a previous (signed-in) state,
      // which causes a 401 on prepare_first_factor.
      const createResult = await signIn.create({
        identifier: emailAddress,
      });
      if (createResult.error) {
        // If the user doesn't exist yet, fall through to sign-up
        if (!isFormIdentifierNotFound(createResult.error)) {
          setErrorMessage(getErrorMessage(createResult.error));
          return false;
        }
      } else {
        // Fresh SignIn created successfully — now send the link
        const signInAttempt = await signIn.emailLink.sendLink({
          emailAddress,
          verificationUrl,
        });
        if (!signInAttempt.error) {
          return true;
        }
        setErrorMessage(getErrorMessage(signInAttempt.error));
        return false;
      }

      // Sign-up branch (user not found)
      const signUpCreateAttempt = await signUp.create({ emailAddress });
      if (signUpCreateAttempt.error) {
        setErrorMessage(getErrorMessage(signUpCreateAttempt.error));
        return false;
      }

      const signUpSendLinkAttempt = await signUp.verifications.sendEmailLink({
        verificationUrl,
      });
      if (signUpSendLinkAttempt.error) {
        setErrorMessage(getErrorMessage(signUpSendLinkAttempt.error));
        return false;
      }

      return true;
    },
    [signIn, signUp, verificationUrl],
  );

  const sendEmailCodeChallenge = useCallback(
    async (emailAddress: string): Promise<EmailCodeBranch | null> => {
      if (!signIn || !signUp) {
        return null;
      }

      const createResult = await signIn.create({
        identifier: emailAddress,
      });
      if (createResult.error) {
        if (!isFormIdentifierNotFound(createResult.error)) {
          setErrorMessage(getErrorMessage(createResult.error));
          return null;
        }

        const signUpCreateAttempt = await signUp.create({ emailAddress });
        if (signUpCreateAttempt.error) {
          setErrorMessage(getErrorMessage(signUpCreateAttempt.error));
          return null;
        }

        const sendAttempt = await signUp.verifications.sendEmailCode();
        if (sendAttempt.error) {
          setErrorMessage(getErrorMessage(sendAttempt.error));
          return null;
        }

        return "sign-up";
      }

      const sendAttempt = await signIn.emailCode.sendCode({ emailAddress });
      if (sendAttempt.error) {
        setErrorMessage(getErrorMessage(sendAttempt.error));
        return null;
      }

      return "sign-in";
    },
    [signIn, signUp],
  );

  const handleGoogleAuth = useCallback(async () => {
    if (!signIn || !signUp) {
      return;
    }

    setErrorMessage(undefined);
    setIsSocialLoading(true);

    try {
      const redirectParams = {
        strategy: "oauth_google",
        redirectCallbackUrl: `${window.location.origin}/callback/sso`,
        redirectUrl: `${window.location.origin}${postAuthUrl()}`,
      } as const;

      const signInAttempt = await signIn.sso(redirectParams);

      if (!signInAttempt.error) {
        return;
      }
      if (!isFormIdentifierNotFound(signInAttempt.error)) {
        setErrorMessage(getErrorMessage(signInAttempt.error));
        return;
      }

      const signUpAttempt = await signUp.sso(redirectParams);
      if (signUpAttempt.error) {
        setErrorMessage(getErrorMessage(signUpAttempt.error));
      }
    } catch (error: unknown) {
      ErrorHandler.logError(error);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSocialLoading(false);
    }
  }, [signIn, signUp, postAuthUrl]);

  const handleSendMagicLink = useCallback(async () => {
    const emailAddress = readTrimmedInput(emailInputRef.current);
    if (!emailAddress) {
      setErrorMessage("Please enter a valid email.");
      return;
    }

    setErrorMessage(undefined);
    setPendingEmailAction("send-link");

    try {
      const ok = await sendEmailMagicLink(emailAddress);
      if (!ok) return;
      setSelectedMethodState("email");
      setEmailSent(true);
      setEmailCodeAwaitingVerify(false);
      setEmailCodeBranch(null);
    } catch (error: unknown) {
      ErrorHandler.logError(error);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setPendingEmailAction(null);
    }
  }, [sendEmailMagicLink]);

  const handleSendEmailCode = useCallback(async () => {
    const emailAddress = readTrimmedInput(emailInputRef.current);
    if (!emailAddress) {
      setErrorMessage("Please enter a valid email.");
      return;
    }

    setErrorMessage(undefined);
    setPendingEmailAction("send-code");

    try {
      const branch = await sendEmailCodeChallenge(emailAddress);
      if (!branch) return;
      setSelectedMethodState("email");
      setEmailSent(false);
      setEmailCodeAwaitingVerify(true);
      setEmailCodeBranch(branch);
    } catch (error: unknown) {
      ErrorHandler.logError(error);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setPendingEmailAction(null);
    }
  }, [sendEmailCodeChallenge]);

  const handleVerifyEmailCode = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!signIn || !signUp || !emailCodeBranch) {
        return;
      }

      const code = readTrimmedInput(codeInputRef.current);
      if (!code) {
        setErrorMessage("Please enter the code from your email.");
        return;
      }

      setErrorMessage(undefined);
      setPendingEmailAction("verify");

      try {
        if (emailCodeBranch === "sign-in") {
          const attempt = await signIn.emailCode.verifyCode({ code });
          if (attempt.error) {
            setErrorMessage(getErrorMessage(attempt.error));
            return;
          }
          if (signIn.status !== "complete") {
            setErrorMessage("Sign-in could not be completed. Try again or use another method.");
            return;
          }
          const finalized = await signIn.finalize();
          if (finalized.error) {
            setErrorMessage(getErrorMessage(finalized.error));
            return;
          }
          // Hard reload so the SpacetimeDB connection boots with the new auth token.
          window.location.assign(postAuthUrl());
          return;
        }

        const attempt = await signUp.verifications.verifyEmailCode({ code });
        if (attempt.error) {
          setErrorMessage(getErrorMessage(attempt.error));
          return;
        }
        if (signUp.status !== "complete") {
          setErrorMessage("Sign-up could not be completed. Try again or use another method.");
          return;
        }
        const finalized = await signUp.finalize();
        if (finalized.error) {
          setErrorMessage(getErrorMessage(finalized.error));
          return;
        }
        window.location.assign(postAuthUrl());
      } catch (error: unknown) {
        ErrorHandler.logError(error);
        setErrorMessage(getErrorMessage(error));
      } finally {
        setPendingEmailAction(null);
      }
    },
    [signIn, signUp, emailCodeBranch, postAuthUrl],
  );

  return {
    state: {
      selectedMethod,
      emailSent,
      emailCodeAwaitingVerify,
      pendingEmailAction,
      isSocialLoading,
      errorMessage,
      defaultEmail,
    },
    actions: {
      setSelectedMethod,
      handleGoogleAuth,
      handleSendMagicLink,
      handleSendEmailCode,
      handleVerifyEmailCode,
    },
    refs: {
      emailInputRef,
      codeInputRefCallback,
    },
  } satisfies AuthnDialogFlow;
}
